import { useEffect, useState } from "react";
import api from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid } from "recharts";
import { Search } from "lucide-react";
import PageHeader from "@/components/PageHeader";
import ProjectSelect from "@/components/ProjectSelect";
import FirmSelect from "@/components/FirmSelect";
import ExcelButton from "@/components/ExcelButton";
import PriceTrend from "@/components/PriceTrend";
import { fmtTL, fmtDate, fmtNum, fmtCompact } from "@/lib/utils";

export default function Analytics() {
  const [projects, setProjects] = useState([]);
  const [firms, setFirms] = useState([]);
  const [projectId, setProjectId] = useState("");
  const [firmId, setFirmId] = useState("");
  const [data, setData] = useState(null);
  const [trendItem, setTrendItem] = useState(null);

  useEffect(() => {
    api.get("/projects").then(r => setProjects(r.data));
    api.get("/firms").then(r => setFirms(r.data));
  }, []);
  useEffect(() => {
    const params = {};
    if (projectId) params.project_id = projectId;
    if (firmId) params.firm_id = firmId;
    api.get("/analytics/summary", { params }).then(r => setData(r.data));
  }, [projectId, firmId]);

  return (
    <div data-testid="analytics-page">
      <PageHeader title="Bütçe ve Üretim Analizi" subtitle="Hakediş verilerinden aylık üretim, firma ve proje dağılımı.">
        <ProjectSelect projects={projects} value={projectId} onChange={setProjectId} testid="analytics-project" />
        <FirmSelect firms={firms} value={firmId} onChange={setFirmId} testid="analytics-firm" />
        <ExcelButton params={{ project_id: projectId, firm_id: firmId }} testid="analytics-excel-button" />
      </PageHeader>
      {data && (
        <>
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
            <Kpi label="Toplam Üretim" value={fmtTL(data.total_amount)} testid="kpi-total" accent />
            <Kpi label="Firma" value={data.firm_count} testid="kpi-firms" />
            <Kpi label="Proje" value={data.project_count} testid="kpi-projects" />
            <Kpi label="Dönem" value={`${fmtDate(data.date_from)} — ${fmtDate(data.date_to)}`} testid="kpi-period" />
          </div>
          <Panel title="Aylık Hakediş Üretimi">
            <ResponsiveContainer width="100%" height={280}>
              <BarChart data={data.monthly}>
                <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" vertical={false} />
                <XAxis dataKey="month" tick={{ fontSize: 11 }} stroke="hsl(var(--muted-foreground))" />
                <YAxis tickFormatter={fmtCompact} tick={{ fontSize: 11 }} stroke="hsl(var(--muted-foreground))" width={60} />
                <Tooltip formatter={(v) => fmtTL(v)} contentStyle={{ background: "hsl(var(--card))", border: "1px solid hsl(var(--border))", borderRadius: 8 }} />
                <Bar dataKey="amount" name="Tutar" fill="#f59e0b" radius={[4, 4, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </Panel>
          <div className="grid lg:grid-cols-2 gap-6 mt-6">
            <Panel title="En Yüksek Hakedişli 10 Firma">
              <ResponsiveContainer width="100%" height={340}>
                <BarChart data={data.top_firms} layout="vertical" margin={{ left: 10 }}>
                  <XAxis type="number" tickFormatter={fmtCompact} tick={{ fontSize: 11 }} stroke="hsl(var(--muted-foreground))" />
                  <YAxis type="category" dataKey="name" width={170} tick={{ fontSize: 10 }} stroke="hsl(var(--muted-foreground))" tickFormatter={(s) => s.length > 26 ? s.slice(0, 26) + "…" : s} />
                  <Tooltip formatter={(v) => fmtTL(v)} contentStyle={{ background: "hsl(var(--card))", border: "1px solid hsl(var(--border))", borderRadius: 8 }} />
                  <Bar dataKey="amount" name="Tutar" fill="#2563eb" radius={[0, 4, 4, 0]} />
                </BarChart>
              </ResponsiveContainer>
            </Panel>
            <Panel title="Proje Bazında Gerçekleşme">
              <div className="max-h-[340px] overflow-y-auto">
                <Table>
                  <TableHeader><TableRow><TableHead>Proje</TableHead><TableHead className="text-right">Firma</TableHead><TableHead className="text-right">Tutar</TableHead></TableRow></TableHeader>
                  <TableBody>{data.projects.map(p => (
                    <TableRow key={p.project_id}><TableCell className="font-medium text-sm">{p.name}<div className="text-xs text-muted-foreground">{fmtDate(p.first)} — {fmtDate(p.last)}</div></TableCell>
                      <TableCell className="text-right font-mono-nums">{p.firm_count}</TableCell>
                      <TableCell className="text-right font-mono-nums font-semibold">{fmtTL(p.amount)}</TableCell></TableRow>
                  ))}</TableBody>
                </Table>
              </div>
            </Panel>
          </div>
        </>
      )}
      <UnitPriceSearch onSelect={setTrendItem} />
      <PriceTrend item={trendItem} onItem={setTrendItem} />
    </div>
  );
}

function UnitPriceSearch({ onSelect }) {
  const [q, setQ] = useState("");
  const [rows, setRows] = useState([]);

  useEffect(() => {
    if (q.trim().length < 2) { setRows([]); return; }
    const t = setTimeout(() => api.get("/analytics/unit-prices", { params: { q } }).then(r => setRows(r.data)), 300);
    return () => clearTimeout(t);
  }, [q]);

  return (
    <Panel title="Birim Fiyat Karşılaştırma" className="mt-6">
      <div className="relative mb-4 max-w-md">
        <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
        <Input className="pl-9" placeholder="İş kalemi veya poz ara (örn: seramik, beton, sıva)" value={q} onChange={e => setQ(e.target.value)} data-testid="unit-price-search" />
      </div>
      {rows.length > 0 ? (
        <div className="max-h-[420px] overflow-y-auto rounded-lg border border-border">
          <Table>
            <TableHeader><TableRow className="bg-muted/40"><TableHead>Tanım</TableHead><TableHead>Firma</TableHead><TableHead>Proje</TableHead>
              <TableHead className="text-right">Birim Fiyat</TableHead><TableHead className="text-right">Miktar</TableHead><TableHead>Dönem</TableHead></TableRow></TableHeader>
            <TableBody>{rows.map((r, i) => (
              <TableRow key={i} data-testid="unit-price-row" className="cursor-pointer hover:bg-amber-500/5" title="Fiyat trendini göster"
                onClick={() => { onSelect({ description: r.description, unit: r.unit }); document.querySelector('[data-testid="price-trend-panel"]')?.scrollIntoView({ behavior: "smooth" }); }}><TableCell className="text-sm">{r.description}<div className="text-xs text-muted-foreground font-mono-nums">{r.poz_no}</div></TableCell>
                <TableCell className="text-xs">{r.firm_name}</TableCell><TableCell className="text-xs">{r.project_name}</TableCell>
                <TableCell className="text-right font-mono-nums font-semibold text-amber-700 dark:text-amber-400">{fmtTL(r.unit_price)}/{r.unit}</TableCell>
                <TableCell className="text-right font-mono-nums">{fmtNum(r.qty)}</TableCell>
                <TableCell className="text-xs text-muted-foreground">{fmtDate(r.first_date)} — {fmtDate(r.last_date)}</TableCell></TableRow>
            ))}</TableBody>
          </Table>
        </div>
      ) : <p className="text-sm text-muted-foreground">{q.trim().length >= 2 ? "Sonuç bulunamadı." : "Aynı işin farklı firma ve projelerdeki birim fiyatlarını görmek için arama yapın."}</p>}
    </Panel>
  );
}

function Panel({ title, children, className = "" }) {
  return (
    <div className={`rounded-xl border border-border bg-card p-5 ${className}`}>
      <h3 className="font-display font-semibold mb-4">{title}</h3>
      {children}
    </div>
  );
}

function Kpi({ label, value, testid, accent }) {
  return (
    <div className={`rounded-xl border p-5 bg-card ${accent ? "border-amber-500/40 bg-amber-500/10" : "border-border"}`} data-testid={testid}>
      <div className="text-xs text-muted-foreground font-medium">{label}</div>
      <div className="text-xl font-display font-extrabold font-mono-nums mt-1">{value}</div>
    </div>
  );
}
